import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2 } from 'lucide-react';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        return;
      } else if (response.data.status === 'expired') {
        setStatus('error');
        return;
      }

      // Continue polling
      setAttempts(prev => prev + 1);
      setTimeout(() => pollPaymentStatus(), 2000);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-blue-50 to-stone-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-12 text-center max-w-md border border-gray-200">
        {status === 'checking' && (
          <>
            <Loader2 className="w-16 h-16 text-blue-600 mx-auto mb-6 animate-spin" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2" style={{ fontFamily: 'Crimson Text, serif' }}>
              Processing Payment
            </h2>
            <p className="text-gray-600">Please wait while we confirm your purchase...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2" style={{ fontFamily: 'Crimson Text, serif' }}>
              Payment Successful!
            </h2>
            <p className="text-gray-600 mb-6">Your purchase is complete. You can now access the full document.</p>
            <div className="flex gap-3">
              <Button
                data-testid="view-purchases-btn"
                onClick={() => navigate('/purchases')}
                className="flex-1 rounded-xl bg-blue-600 hover:bg-blue-700"
              >
                View Purchases
              </Button>
              <Button
                data-testid="continue-shopping-btn"
                onClick={() => navigate('/')}
                variant="outline"
                className="flex-1 rounded-xl"
              >
                Continue Shopping
              </Button>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-3xl">❌</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2" style={{ fontFamily: 'Crimson Text, serif' }}>
              Payment Failed
            </h2>
            <p className="text-gray-600 mb-6">There was an issue processing your payment. Please try again.</p>
            <Button
              data-testid="try-again-btn"
              onClick={() => navigate('/')}
              className="w-full rounded-xl bg-blue-600 hover:bg-blue-700"
            >
              Back to Marketplace
            </Button>
          </>
        )}

        {status === 'timeout' && (
          <>
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-3xl">⏱️</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2" style={{ fontFamily: 'Crimson Text, serif' }}>
              Status Check Timeout
            </h2>
            <p className="text-gray-600 mb-6">
              We couldn't confirm your payment status. Please check your email or purchase history.
            </p>
            <div className="flex gap-3">
              <Button
                data-testid="check-purchases-btn"
                onClick={() => navigate('/purchases')}
                className="flex-1 rounded-xl bg-blue-600 hover:bg-blue-700"
              >
                Check Purchases
              </Button>
              <Button
                data-testid="home-btn"
                onClick={() => navigate('/')}
                variant="outline"
                className="flex-1 rounded-xl"
              >
                Home
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
